import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PublicHTTPService } from '../../../services/publicHttpService';
import JobviousLogo from "./../../../assets/Jobvious_logo@2x.svg";
import { convertFromUTC } from '../../../util/convert_timezone';

const API = new PublicHTTPService();

const PublicQuoteAccept = () => {
  const { requestId, token } = useParams();
  const [step, setStep] = useState('loading'); // loading, quote, accepting, accepted, error
  const [quoteData, setQuoteData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuoteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, token]);

  const loadQuoteData = async () => {
    try {
      setStep('loading');
      const response = await API.get(`/public/quote/${requestId}/${token}`);

      if (response.data.success) {
        setQuoteData(response.data.quote);

        // Check if already accepted
        if (response.data.quote.quoteAcceptedAt) {
          setStep('already_accepted');
        } else {
          setStep('quote');
        }
      } else {
        setError(response.data.message || 'Unable to load estimate');
        setStep('error');
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      setError('Unable to load estimate. The link may be invalid or expired.');
      setStep('error');
    }
  };

  const handleAccept = async () => {
    try {
      setStep('accepting');
      const response = await API.post(`/public/quote/${requestId}/${token}/accept`);

      if (response.data.success) {
        setStep('accepted');
      } else {
        setError(response.data.message || 'Unable to accept estimate');
        setStep('error');
      }
    } catch (err) {
      console.error('Error accepting quote:', err);
      setError('Unable to accept estimate. Please try again or contact the business.');
      setStep('error');
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-3">Loading your estimate...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="alert alert-danger">
              <i className="fa-sharp fa-solid fa-exclamation-circle"></i> {error}
            </div>
            <p>If you believe this is an error, please contact the business directly.</p>
          </div>
        </div>
      </div>
    );
  }

  // Already accepted state
  if (step === 'already_accepted') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="alert alert-success">
              <i className="fa-sharp fa-solid fa-check-circle"></i>{' '}
              This estimate was already accepted on {convertFromUTC(quoteData.quoteAcceptedAt).format("MMMM D, YYYY")}
            </div>
            <p>Your appointment has been scheduled. The business will contact you with details.</p>
          </div>
        </div>
      </div>
    );
  }

  // Accepting state
  if (step === 'accepting') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="spinner-border text-success" role="status">
              <span className="sr-only">Processing...</span>
            </div>
            <p className="mt-3">Confirming your appointment...</p>
          </div>
        </div>
      </div>
    );
  }

  // Accepted state
  if (step === 'accepted') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="card shadow">
              <div className="card-body">
                <div className="text-success mb-4">
                  <i className="fa-sharp fa-solid fa-check-circle" style={{ fontSize: '64px' }}></i>
                </div>
                <h2>Appointment Confirmed!</h2>
                <p className="lead">
                  Your appointment has been scheduled for{' '}
                  <strong>{convertFromUTC(quoteData.availableDate).format("MMMM D, YYYY")}</strong>
                </p>
                <hr />
                <p>
                  <strong>{quoteData.installerName}</strong> will contact you to confirm the details.
                </p>
                <p className="text-muted">
                  A confirmation email has been sent to your email address.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quote display state
  return (
    <div className="container mt-5 mb-5">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-8">
          <div className="text-center mb-4">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px' }} />
          </div>

          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Your Estimate</h4>
            </div>
            <div className="card-body">
              {/* Business Info */}
              <div className="mb-4">
                <h5>{quoteData.installerName}</h5>
                {quoteData.installerPhone && <p className="mb-1"><i className="fa-sharp fa-solid fa-phone"></i> {quoteData.installerPhone}</p>}
                {quoteData.installerEmail && <p className="mb-0"><i className="fa-sharp fa-solid fa-envelope"></i> {quoteData.installerEmail}</p>}
              </div>

              <hr />

              {/* Vehicle Info */}
              <div className="mb-4">
                <h6>Vehicle</h6>
                <p className="mb-0">
                  <strong>{quoteData.vehicleDescription}</strong>
                  {quoteData.vin && <span className="text-muted ml-2">VIN: {quoteData.vin}</span>}
                </p>
              </div>

              <hr />

              {/* Services */}
              <div className="mb-4">
                <h6>Services</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteData.services && quoteData.services.map((service, index) => (
                      <tr key={index}>
                        <td>{(service.category || service.service_category) ? `${service.category || service.service_category} - ` : ''}{service.customDescription || service.name}</td>
                        <td className="text-right">{formatCurrency(service.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {quoteData.taxes > 0 && (
                      <>
                        <tr>
                          <td>Subtotal</td>
                          <td className="text-right">{formatCurrency(quoteData.total)}</td>
                        </tr>
                        <tr>
                          <td>Tax</td>
                          <td className="text-right">{formatCurrency(quoteData.taxes)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="font-weight-bold">
                      <td>Total</td>
                      <td className="text-right">{formatCurrency((quoteData.total || 0) + (quoteData.taxes || 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <hr />

              {/* Proposed Dates */}
              <div className="mb-4">
                <h6>Proposed Work Date</h6>
                <p className="mb-0">
                  <i className="fa-sharp fa-solid fa-calendar"></i>{' '}
                  <strong>{convertFromUTC(quoteData.availableDate).format("dddd, MMMM D, YYYY")}</strong>
                  {quoteData.dueDate && quoteData.dueDate !== quoteData.availableDate && (
                    <span> - {convertFromUTC(quoteData.dueDate).format("dddd, MMMM D, YYYY")}</span>
                  )}
                </p>
              </div>

              {/* Notes */}
              {quoteData.notes && (
                <>
                  <hr />
                  <div className="mb-4">
                    <h6>Notes</h6>
                    <p className="mb-0">{quoteData.notes}</p>
                  </div>
                </>
              )}

              <hr />

              {/* Actions */}
              <div className="text-center">
                <p className="mb-3">Ready to book this appointment?</p>
                <button
                  className="btn btn-success btn-lg mr-3"
                  onClick={handleAccept}
                >
                  <i className="fa-sharp fa-solid fa-check"></i> Accept & Book Appointment
                </button>
                <p className="mt-3 text-muted">
                  <small>
                    Need a different date?{' '}
                    <a href={`mailto:${quoteData.installerEmail}?subject=Reschedule Request - ${quoteData.vehicleDescription}`}>
                      Contact us to reschedule
                    </a>
                  </small>
                </p>
              </div>
            </div>
            <div className="card-footer text-muted text-center">
              <small>
                Estimate sent {convertFromUTC(quoteData.quoteSentAt).format("MMMM D, YYYY")}
                {quoteData.expiresAt && (
                  <span> | Valid until {convertFromUTC(quoteData.expiresAt).format("MMMM D, YYYY")}</span>
                )}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicQuoteAccept;
